import type { ImportJobStatus } from '@/lib/api-client';

interface StatusBadgeProps {
  status: ImportJobStatus;
}

const STATUS_LABELS: Record<ImportJobStatus, string> = {
  pending: 'Aguardando',
  parsing: 'Lendo arquivo',
  preview: 'Pronto para importar',
  importing: 'Importando',
  done: 'Concluído',
  failed: 'Falhou',
};

const STATUS_COLORS: Record<ImportJobStatus, string> = {
  pending: 'bg-zinc-500/15 text-zinc-400',
  parsing: 'bg-blue-500/15 text-blue-400',
  preview: 'bg-violet-500/15 text-violet-400',
  importing: 'bg-amber-500/15 text-amber-400',
  done: 'bg-emerald-500/15 text-emerald-500',
  failed: 'bg-red-500/15 text-red-400',
};

export function StatusBadge({ status }: StatusBadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        STATUS_COLORS[status],
      ].join(' ')}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

interface ImportProgressProps {
  totalRows: number | null;
  processedRows: number | null;
  successRows: number | null;
  skippedRows: number | null;
  errorRows: number | null;
}

export function ImportProgress({
  totalRows,
  processedRows,
  successRows,
  skippedRows,
  errorRows,
}: ImportProgressProps) {
  if (totalRows === null) return null;

  const pct =
    totalRows > 0 && processedRows !== null
      ? Math.round((processedRows / totalRows) * 100)
      : 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-xs text-[var(--fg-muted)]">
        <span>
          {processedRows ?? 0} / {totalRows} linhas
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color-mix(in_oklch,var(--fg)_10%,transparent)]">
        <div
          className="h-full rounded-full bg-[var(--accent)] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {(successRows !== null || errorRows !== null) && (
        <div className="flex gap-3 text-xs">
          {successRows !== null && successRows > 0 && (
            <span className="text-emerald-500">{successRows} importados</span>
          )}
          {skippedRows !== null && skippedRows > 0 && (
            <span className="text-zinc-400">{skippedRows} ignorados</span>
          )}
          {errorRows !== null && errorRows > 0 && (
            <span className="text-red-400">{errorRows} com erro</span>
          )}
        </div>
      )}
    </div>
  );
}
